import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { analyticsService } from '../services/analyticsService';
import './LegalPage.css';

const TermsOfService = () => {
  useEffect(() => {
    analyticsService.trackPageView('terms-of-service');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-header">
        <Link to="/" className="back-link">← Back to Home</Link>
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: November 22, 2025</p>
      </div>

      <div className="legal-content">
        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            Welcome to Learn for Fun ("Platform", "Service", "we", "us", or "our"). By accessing or using our
            educational platform at learnforfun.online, you agree to be bound by these Terms of Service ("Terms").
            If you do not agree to these Terms, please do not use our Service.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            Learn for Fun provides online educational services, including but not limited to:
          </p>
          <ul>
            <li>Live, coach-led STEM learning sessions</li>
            <li>Interactive coding and programming courses</li>
            <li>Virtual classrooms with real-time collaboration</li>
            <li>Homework assignments and progress tracking</li>
            <li>AI, robotics, and creative coding workshops</li>
          </ul>
        </section>

        <section>
          <h2>3. User Accounts</h2>
          <h3>3.1 Account Registration</h3>
          <p>
            To access certain features, you must create an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and promptly update your account information</li>
            <li>Keep your password secure and confidential</li>
            <li>Accept responsibility for all activities that occur under your account</li>
            <li>Notify us immediately of any unauthorized access or security breach</li>
          </ul>

          <h3>3.2 Account Types</h3>
          <p>
            We offer different account types:
          </p>
          <ul>
            <li><strong>Student Accounts:</strong> For learners to register for sessions and track progress</li>
            <li><strong>Coach Accounts:</strong> For instructors to manage sessions and student progress</li>
            <li><strong>Admin Accounts:</strong> For platform management (invitation only)</li>
          </ul>

          <h3>3.3 Age Requirements</h3>
          <p>
            Students under 13 years old must have parental consent to use our Service. Parents or guardians
            are responsible for monitoring their child's use of the Platform.
          </p>
        </section>

        <section>
          <h2>4. Session Registration and Enrollment</h2>
          <h3>4.1 Registration Process</h3>
          <p>
            Students may register for available sessions through the Platform. Registration is subject to:
          </p>
          <ul>
            <li>Session availability and capacity limits</li>
            <li>Admin or coach approval</li>
            <li>Payment of any applicable fees</li>
          </ul>

          <h3>4.2 Session Fees</h3>
          <p>
            Some sessions may require payment. Fees, payment terms, and refund policies will be clearly
            communicated before registration. By registering for a paid session, you agree to pay all
            applicable fees.
          </p>

          <h3>4.3 Cancellation Policy</h3>
          <p>
            Students may cancel their session registration according to the cancellation terms specified
            for each session. Refunds, if applicable, will be processed according to our refund policy.
          </p>
        </section>

        <section>
          <h2>5. User Conduct and Content</h2>
          <h3>5.1 Acceptable Use</h3>
          <p>
            You agree to use the Platform only for lawful purposes. You shall not:
          </p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on the intellectual property rights of others</li>
            <li>Transmit harmful, offensive, or inappropriate content</li>
            <li>Harass, bully, or threaten other users</li>
            <li>Disrupt or interfere with the Platform's operation</li>
            <li>Attempt to gain unauthorized access to any part of the Platform</li>
            <li>Use the Platform for any commercial purposes without our consent</li>
          </ul>

          <h3>5.2 User-Generated Content</h3>
          <p>
            Users may submit homework, code, projects, and other content ("User Content"). By submitting
            User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, display,
            and distribute your content for educational purposes on the Platform.
          </p>

          <h3>5.3 Content Monitoring</h3>
          <p>
            We reserve the right to monitor, review, and remove any User Content that violates these Terms
            or is otherwise objectionable.
          </p>
        </section>

        <section>
          <h2>6. Virtual Classroom Guidelines</h2>
          <p>
            When participating in live virtual classrooms, users must:
          </p>
          <ul>
            <li>Follow coach instructions and classroom rules</li>
            <li>Maintain a respectful and collaborative environment</li>
            <li>Use appropriate language and behavior</li>
            <li>Respect the privacy of other participants</li>
            <li>Not record or share classroom sessions without permission</li>
          </ul>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          <h3>7.1 Platform Content</h3>
          <p>
            All content, features, and functionality on the Platform, including but not limited to text,
            graphics, logos, course materials, and software, are owned by Learn for Fun or its licensors
            and are protected by copyright, trademark, and other intellectual property laws.
          </p>

          <h3>7.2 Limited License</h3>
          <p>
            We grant you a limited, non-exclusive, non-transferable license to access and use the Platform
            for personal, educational purposes only.
          </p>

          <h3>7.3 Student Work</h3>
          <p>
            Students retain ownership of their original work created on the Platform, but grant us the
            right to use it for educational and promotional purposes.
          </p>
        </section>

        <section>
          <h2>8. Payment and Billing</h2>
          <p>
            For paid services:
          </p>
          <ul>
            <li>All fees are in USD unless otherwise specified</li>
            <li>Payment is due at the time of registration or as specified</li>
            <li>We use secure third-party payment processors</li>
            <li>Refunds are subject to our refund policy</li>
            <li>You are responsible for any applicable taxes</li>
          </ul>
        </section>

        <section>
          <h2>9. Privacy and Data Protection</h2>
          <p>
            Your use of the Platform is subject to our <Link to="/privacy-policy">Privacy Policy</Link>,
            which explains how we collect, use, and protect your information. By using the Platform, you
            consent to our data practices as described in the Privacy Policy.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Services</h2>
          <p>
            The Platform may integrate with third-party services (e.g., Firebase, Google services).
            Your use of these services is subject to their respective terms and conditions. We are not
            responsible for third-party services or their content.
          </p>
        </section>

        <section>
          <h2>11. Disclaimers and Limitations of Liability</h2>
          <h3>11.1 Service "As Is"</h3>
          <p>
            THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR
            A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
          </p>

          <h3>11.2 Educational Outcomes</h3>
          <p>
            We do not guarantee specific educational outcomes or results. Learning outcomes depend on
            individual effort, engagement, and other factors beyond our control.
          </p>

          <h3>11.3 Limitation of Liability</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE PLATFORM.
          </p>
        </section>

        <section>
          <h2>12. Termination</h2>
          <p>
            We reserve the right to suspend or terminate your account and access to the Platform at any
            time, with or without notice, for:
          </p>
          <ul>
            <li>Violation of these Terms</li>
            <li>Fraudulent or illegal activity</li>
            <li>Behavior that harms other users or the Platform</li>
            <li>Non-payment of fees</li>
          </ul>
          <p>
            Upon termination, your right to access the Platform will immediately cease.
          </p>
        </section>

        <section>
          <h2>13. Modifications to Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify users of material changes by
            posting the updated Terms on the Platform with a new "Last Updated" date. Your continued
            use of the Platform after changes constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section>
          <h2>14. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms are governed by and construed in accordance with the laws of the United States.
            Any disputes arising from these Terms or your use of the Platform shall be resolved through
            binding arbitration, except where prohibited by law.
          </p>
        </section>

        <section>
          <h2>15. Contact Information</h2>
          <p>
            If you have questions about these Terms, please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>Learn for Fun</strong></p>
            <p>Email: info@learnforfun.online</p>
            <p>Website: learnforfun.online</p>
          </div>
        </section>

        <section>
          <h2>16. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision will
            be limited or eliminated to the minimum extent necessary, and the remaining provisions will
            remain in full force and effect.
          </p>
        </section>

        <section>
          <h2>17. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you
            and Learn for Fun regarding your use of the Platform.
          </p>
        </section>

        <div className="acceptance-note">
          <p>
            <strong>By using Learn for Fun, you acknowledge that you have read, understood, and agree
            to be bound by these Terms of Service.</strong>
          </p>
        </div>
      </div>

      <div className="legal-footer">
        <Link to="/">Return to Home</Link>
        <span>•</span>
        <Link to="/privacy-policy">Privacy Policy</Link>
      </div>
    </div>
  );
};

export default TermsOfService;
